import * as maptilersdk from '@maptiler/sdk';
import { Vendor, LocationGroup, EnhancedMarkerElement } from '../types/types';
import { MAP_EASE_TO_DURATION, TOUCH_CONFIG } from './appConstants';
import { logger } from './logger';
import CircularText from '../components/Map/MarkerStyles/CircularText';
import { createRoot } from 'react-dom/client';
import React from 'react';
import { MarkerManager, markerManager } from './markerManager';

interface MarkerCollision {
  marker: maptilersdk.Marker;
  distance: number;
  coords: [number, number];
}

// Add debug styles for touch areas
export const addTouchDebugStyles = (): void => {
  if (!document.getElementById('touch-debug-styles')) {
    const style = document.createElement('style');
    style.id = 'touch-debug-styles';
    style.textContent = `
      .marker-touch-area {
        position: absolute;
        border: ${TOUCH_CONFIG.DEBUG_MODE ? '2px dashed rgba(255, 0, 0, 0.3)' : 'none'};
        border-radius: 50%;
        background: ${TOUCH_CONFIG.DEBUG_MODE ? 'rgba(255, 0, 0, 0.1)' : 'transparent'};
        pointer-events: auto;
        z-index: 1000;
        display: block;
      }

      .marker-visual {
        position: absolute;
        pointer-events: none;
        z-index: 1001;
      }

      .marker-container {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .circular-text-container {
        transition: opacity 0.25s ease, transform 0.25s ease;
      }

      .circular-text-container.hidden {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.6);
        pointer-events: none;
      }

      .circular-text-container.visible {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
      }
    `;
    document.head.appendChild(style);
  }
};

// Calculate optimal touch area size
const calculateTouchAreaSize = (visualSize: number): number => {
  return Math.max(TOUCH_CONFIG.MIN_TOUCH_SIZE, visualSize * 1.2);
};

// Enhanced marker element creation with separate touch and visual areas
export const createEnhancedMarkerElement = (
  vendor: Vendor,
  _currentZoom: number,
  eventCount = 1,
  getMarkerStyles: (isOpen: boolean, isFree: boolean) => Record<string, string>,
  isFree = false
): EnhancedMarkerElement => {
  // Create container for marker
  const container = document.createElement('div') as EnhancedMarkerElement;
  container.className = 'marker-container';

  // Get visual marker size
  const styles = getMarkerStyles(false, isFree);
  const visualSize = parseInt(styles.width);
  const touchSize = calculateTouchAreaSize(visualSize);

  // Create touch area (larger, invisible/debug)
  const touchArea = document.createElement('div');
  touchArea.className = 'marker-touch-area';
  touchArea.style.cssText = `
    width: ${touchSize}px;
    height: ${touchSize}px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `;

  // Create visual marker (centered within touch area)
  const visualMarker = document.createElement('div');
  visualMarker.className = 'marker-visual';
  Object.assign(visualMarker.style, {
    ...styles,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    position: 'absolute',
  });

  // Add event count text to visual marker if needed
  if (eventCount > 1) {
    visualMarker.textContent = eventCount.toString();
  }

  // Add GPS pulse effect if needed
  if (vendor.uses_gps) {
    const pulseRing = document.createElement('div');
    pulseRing.className = 'pulse-ring';
    const pulseDot = document.createElement('div');
    pulseDot.className = 'pulse-dot';
    visualMarker.appendChild(pulseRing);
    visualMarker.appendChild(pulseDot);
  }

  // Assemble marker
  container.appendChild(touchArea);
  container.appendChild(visualMarker);

  // Add CircularText for free events when zoomed in
  if (isFree) {
    const circularTextContainer = document.createElement('div');
    circularTextContainer.className = 'circular-text-container hidden';

    // Use touchSize for the radius, scaled down
    const textRadius = touchSize * 1.2; // 80% of touch area

    circularTextContainer.style.cssText = `
      position: absolute;
      width: ${textRadius}px;
      height: ${textRadius}px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      pointer-events: none;
      z-index: 5;
    `;

    container.appendChild(circularTextContainer);

    // Render CircularText React component
    const root = createRoot(circularTextContainer);

    // Register root for cleanup
    markerManager.registerCircularTextRoot(root);

    root.render(
      React.createElement(CircularText, {
        text: 'free • free • ',
        spinDuration: 8,
        className: 'circular-text-free',
        radius: textRadius,
      })
    );
  }

  // Add CircularText for user's own events with custom circular_text metadata
  const circularText = vendor.metadata?.circular_text;
  if (circularText && vendor.is_own_event) {
    const userCircularTextContainer = document.createElement('div');
    userCircularTextContainer.className = 'circular-text-container hidden';

    const textRadius = touchSize * 1.2;

    userCircularTextContainer.style.cssText = `
      position: absolute;
      width: ${textRadius}px;
      height: ${textRadius}px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      pointer-events: none;
      z-index: 5;
    `;

    container.appendChild(userCircularTextContainer);

    const userRoot = createRoot(userCircularTextContainer);
    markerManager.registerCircularTextRoot(userRoot);

    userRoot.render(
      React.createElement(CircularText, {
        text: circularText,
        spinDuration: 15,
        className: 'circular-text-user',
        radius: textRadius,
      })
    );
  }

  // Store metadata for collision detection
  container._ceenTouchSize = touchSize;
  container._ceenVisualSize = visualSize;
  container._ceenVendor = vendor;

  return container;
};

// Check for touch area collisions
const checkTouchAreaCollisions = (
  markers: maptilersdk.Marker[],
  targetMarker: maptilersdk.Marker,
  targetCoords: [number, number]
): MarkerCollision[] => {
  const targetElement = targetMarker.getElement() as EnhancedMarkerElement;
  const targetSize = targetElement._ceenTouchSize / 2; // radius
  const collisions: MarkerCollision[] = [];

  markers.forEach(marker => {
    if (marker === targetMarker) return;

    const markerCoords = marker.getLngLat();
    const markerElement = marker.getElement() as EnhancedMarkerElement;
    const markerSize = markerElement._ceenTouchSize / 2; // radius

    // Convert coordinate distance to screen pixels (approximate)
    const map = (marker as any)._map as maptilersdk.Map;
    if (!map) return;

    const targetPoint = map.project(targetCoords);
    const markerPoint = map.project([markerCoords.lng, markerCoords.lat]);

    const distance = Math.sqrt(
      Math.pow(targetPoint.x - markerPoint.x, 2) + Math.pow(targetPoint.y - markerPoint.y, 2)
    );

    const combinedRadius = targetSize + markerSize;

    if (distance < combinedRadius + TOUCH_CONFIG.COLLISION_THRESHOLD) {
      collisions.push({
        marker,
        distance,
        coords: [markerCoords.lng, markerCoords.lat],
      });
    }
  });

  return collisions;
};

// Smart disambiguation zoom
const performDisambiguationZoom = (
  map: maptilersdk.Map,
  _collisions: MarkerCollision[],
  targetCoords: [number, number]
): boolean => {
  logger.log('🎯 performDisambiguationZoom called');

  const currentZoom = map.getZoom();

  if (currentZoom >= TOUCH_CONFIG.MAX_DISAMBIGUATION_ZOOM) {
    logger.log('Max zoom reached, cannot disambiguate further');
    return false;
  }

  const newZoom = Math.min(
    currentZoom + TOUCH_CONFIG.ZOOM_INCREMENT,
    TOUCH_CONFIG.MAX_DISAMBIGUATION_ZOOM
  );

  logger.log(`Disambiguating: zooming from ${currentZoom} to ${newZoom}`);

  // Set flag BEFORE starting zoom
  (map as any)._ceenDisambiguationZoom = true;
  logger.log('🚩 Disambiguation flag SET, value now:', (map as any)._ceenDisambiguationZoom);

  // Clear flag when animation completes, not on arbitrary timer
  // Add a small delay to ensure the flag persists through the viewport change delay (300ms)
  const clearFlag = () => {
    setTimeout(() => {
      logger.log('🚩 Disambiguation flag CLEARED after moveend + safety buffer');
      (map as any)._ceenDisambiguationZoom = false;
    }, 400); // 400ms buffer to ensure it outlasts VIEWPORT_CHANGE_DELAY (300ms)
    map.off('moveend', clearFlag);
  };

  // Set up listener to clear flag when animation completes
  map.once('moveend', clearFlag);

  map.easeTo({
    center: targetCoords,
    zoom: newZoom,
    duration: MAP_EASE_TO_DURATION,
    padding: { top: 50, bottom: 50, left: 50, right: 50 },
  });

  return true;
};

// Enhanced marker interaction handler
export const createEnhancedMarkerHandler = (
  marker: maptilersdk.Marker,
  group: LocationGroup,
  popup: maptilersdk.Popup,
  _onMarkerClick: ((vendorId: number, displayId?: string, shouldPan?: boolean) => void) | undefined,
  markerManager: MarkerManager,
  map: React.MutableRefObject<maptilersdk.Map | null>,
  isProgrammaticMove: React.MutableRefObject<boolean>
) => {
  return (e: Event) => {
    e.stopPropagation();
    e.preventDefault();

    const now = Date.now();
    if (now - ((marker as any)._lastClickTime || 0) < 300) {
      return; // Debounce
    }
    (marker as any)._lastClickTime = now;

    // Check for touch area collisions
    const collisions = checkTouchAreaCollisions(markerManager.getAllMarkers(), marker, [
      group.coordinates[0],
      group.coordinates[1],
    ]);

    logger.log(`Touch detected on marker ${group.vendors[0].id}:`, {
      collisions: collisions.length,
      touchSize: (marker.getElement() as EnhancedMarkerElement)._ceenTouchSize,
    });

    // If there are collisions, try to zoom to disambiguate
    if (collisions.length > 0 && map.current) {
      const didZoom = performDisambiguationZoom(map.current, collisions, [
        group.coordinates[0],
        group.coordinates[1],
      ]);

      if (didZoom) {
        logger.log('Disambiguation zoom triggered, waiting for second touch');
        return; // Wait for zoom to complete and user to touch again
      }
    }

    // No collisions or zoom failed - proceed with normal marker interaction
    logger.log('Processing marker touch - no collisions or post-disambiguation');

    // Set programmatic move flag
    isProgrammaticMove.current = true;
    logger.log('🚩 isProgrammaticMove flag SET to true');

    (marker as any)._popupShouldExpand = false;

    // Close other popups
    markerManager.getAllMarkers().forEach(m => {
      if (m !== marker && m.getPopup()?.isOpen()) {
        m.getPopup()?.remove();
      }
    });

    // Pan to marker and open popup
    if (map.current) {
      const container = map.current.getContainer();
      const containerHeight = container.clientHeight;
      const offsetY = containerHeight * 0.3;

      // Ensure all padding values are positive
      const topPadding = 100 + offsetY;
      const bottomPadding = Math.max(50, 100 - offsetY); // Ensure minimum 50px bottom padding

      map.current.easeTo({
        center: [group.coordinates[0], group.coordinates[1]],
        duration: MAP_EASE_TO_DURATION,
        padding: {
          top: topPadding,
          bottom: bottomPadding,
          left: 50,
          right: 50,
        },
      });

      map.current.once('moveend', () => {
        // Delay clearing the flag to ensure handleViewportChange sees it
        setTimeout(() => {
          isProgrammaticMove.current = false;
        }, 100);

        if (!popup.isOpen()) {
          marker.togglePopup();
        }
      });
    }
  };
};

// Modified bounds change detection to ignore disambiguation zooms
export const shouldIgnoreBoundsChange = (map: maptilersdk.Map): boolean => {
  return (map as any)._ceenDisambiguationZoom === true;
};
